import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import ChaosHeader from "@/components/ChaosHeader";
import MarketingLanding from "@/components/MarketingLanding";
import AuthModal from "@/components/AuthModal";
import ScanInterface from "@/components/ScanInterface";
import PWAInstallGuide from "@/components/PWAInstallGuide";
import AppFooter from "@/components/AppFooter";
import AuthCallback from "@/components/AuthCallback";
import ProfilePage from "@/pages/ProfilePage";
import ReviewPage from "@/pages/ReviewPage";
import UpdatesPage from "@/pages/UpdatesPage";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { FastCheckProvider } from "@/contexts/FastCheckContext";
import FastCheckPanel from "@/components/FastCheckPanel";
import { useIsMobile } from "@/hooks/useMediaQuery";
const Loader = () => (
  <div className="min-h-[100dvh] w-full chaos-bg-gradient flex items-center justify-center">
    <div className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.28em] text-[#cbd5e1]">
      <span className="inline-block w-2 h-2 bg-[#FF0033] chaos-pulse" />
      Booting validation node…
    </div>
  </div>
);

const ScanWorkspace = () => {
  return (
    <div
      data-testid="app-root"
      className="chaos-grain chaos-bg-gradient min-h-[100dvh] w-full text-[#f1f5f9]"
    >
      <ChaosHeader showCredits />
      <ScanInterface />
      <AppFooter />
      <PWAInstallGuide />
    </div>
  );
};

/**
 * View routing for `/`:
 *  - MOBILE: ALWAYS scan workspace (cold).
 *  - DESKTOP authed: scan workspace.
 *  - DESKTOP anonymous: marketing landing → AuthModal.
 */
const Home = () => {
  const { status, authModalOpen, openAuthModal, closeAuthModal } = useAuth();
  const isMobile = useIsMobile();

  if (status === "loading") return <Loader />;

  if (isMobile || status === "authed") {
    return (
      <>
        <ScanWorkspace />
        <AuthModal open={authModalOpen} onOpenChange={closeAuthModal} />
      </>
    );
  }

  // Desktop anonymous
  return (
    <div
      data-testid="app-root"
      className="chaos-grain chaos-bg-gradient min-h-[100dvh] w-full text-[#f1f5f9]"
    >
      <MarketingLanding onLaunch={openAuthModal} />
      <AuthModal open={authModalOpen} onOpenChange={closeAuthModal} />
    </div>
  );
};

const RoutedApp = () => {
  if (
    typeof window !== "undefined" &&
    window.location.hash?.includes("session_id=")
  ) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/profile" element={<ProfilePage />} />
      <Route path="/review" element={<ReviewPage />} />
      <Route path="/updates" element={<UpdatesPage />} />
      <Route path="*" element={<Home />} />
    </Routes>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <FastCheckProvider>
              <RoutedApp />
              <FastCheckPanel variant="floating" />
            </FastCheckProvider>
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
